import { useState } from "react"
import { motion } from "framer-motion"
import { Check } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

const languages = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
]

const SelectLanguagePage = () => {
  const { i18n } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState("en") // Default UI selection, not saved to localStorage
  const navigate = useNavigate()

  // These translations are hardcoded since we don't have a language set yet
  const translations = {
    en: {
      title: "Choose Your Language",
      continue: "Continue",
    },
    hi: {
      title: "अपनी भाषा चुनें",
      continue: "जारी रखें",
    },
  }

  const handleLanguageChange = (languageCode) => {
    setSelectedLanguage(languageCode)
  }

  const handleContinue = () => {
    // Only save to localStorage when the user clicks Continue
    localStorage.setItem("language", selectedLanguage)

    // Now change the i18n language
    i18n.changeLanguage(selectedLanguage)

    // Redirect to login if not authenticated, or home if authenticated
    const isAuthenticated = !!localStorage.getItem("token")
    navigate(isAuthenticated ? "/" : "/login")
  }

  // Get translations based on the currently selected language in the UI
  const t = (key) => {
    const keys = key.split(".")
    let result = translations[selectedLanguage]

    for (const k of keys) {
      if (result && result[k]) {
        result = result[k]
      } else {
        return key // Fallback to the key if translation not found
      }
    }

    return result
  }

  return (
    <div className="min-h-screen bg-gray-100 font-sans flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-lg shadow-md p-8 w-full max-w-md"
      >
        <h1 className="text-3xl font-bold text-primary-100 mb-6 text-center">{t("title")}</h1>
        <div className="space-y-4 mb-8">
          {languages.map((language) => (
            <motion.button
              key={language.code}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleLanguageChange(language.code)}
              className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
                selectedLanguage === language.code
                  ? "bg-primary-100/10 border border-primary-100 text-primary-100"
                  : "bg-white hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <div className="flex flex-col items-start">
                <span className="font-medium">{language.name}</span>
                <span className="text-sm text-gray-500">{language.nativeName}</span>
              </div>
              {selectedLanguage === language.code && <Check className="w-5 h-5 text-primary-100" />}
            </motion.button>
          ))}
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleContinue}
          className="w-full bg-primary-100 text-white py-3 rounded-lg font-semibold hover:bg-primary-100/90 transition-colors"
        >
          {t("continue")}
        </motion.button>
      </motion.div>
    </div>
  )
}

export default SelectLanguagePage
